import { type SegmentedControlOption } from '@air/component-segmented-control';

import {
  type ShareCustomColors,
  type ShareDesignColorRole,
} from '~/components/ShareModal/components/ShareDesignColorRow';
import { COLOR_MODE_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import { PAGE_WIDTH_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';
import { THUMBNAIL_RADIUS_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { type usePrivateContextSourcesGet } from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';

/**
 * Share-link-generic design resolution logic, shared by every share-link design authoring surface:
 * the board share modal's Design tab (`ShareBoardDesignTab`), the collection create modal
 * (`CreateShowcaseDesignProvider`), the collection link editor
 * (`ShowcaseEditorDesignProvider`), and the shared rows in `ShareModal/components/`.
 * Brand-context mapping, the hex resolution chain, defaults, and the option arrays live here once;
 * collection-only design config (cover width, the collection role-config builder) stays in
 * `ShowcaseModal/showcaseDesignColorResolution.ts`.
 * Keep this file pure (no React, no hooks).
 */

/** Stored color roles the brand-context auto-map fills (and clears) as one unit. */
export type BrandMappedColorRole = 'headerBgColor' | 'buttonBgColor' | 'headlineBgColor' | 'pageBgColor';

/**
 * Keyword → role lookup driving the brand-context auto-map. We don't have an explicit role tag on
 * brand colors, so each Context Source color value is matched by name/label keyword (first match
 * wins, and a value is never reused for two roles). `buttonBgColor` and `headlineBgColor` share one
 * keyword list because one stored hex authors both public roles (buttons & headlines stay matched).
 */
export const BRAND_CONTEXT_ROLE_KEYWORDS: Record<'headerBgColor' | 'buttonBgColor' | 'pageBgColor', readonly string[]> =
  {
    headerBgColor: ['banner', 'header', 'nav'],
    buttonBgColor: ['button', 'primary', 'cta', 'headline', 'heading', 'title'],
    pageBgColor: ['background', 'bg', 'surface', 'canvas', 'page'],
  };

/**
 * Display-only hexes resolved from the brand context, keyed by stored color role. Typed over every
 * role (not just the matched three) so each color row can index it by its `readKey` directly; the
 * map only ever holds entries for the three displayed roles the auto-map fills.
 */
export type BrandContextHexByRole = Partial<Record<keyof ShareCustomColors, string>>;

export type BrandColorMappingResult = {
  /**
   * contextSource refs to merge into `customColors`, keyed by stored role. Output-shaped (carry the
   * resolved `hex`) so both the create draft setter and the edit live-save accept them with no cast.
   */
  nextRoles: Partial<Record<BrandMappedColorRole, ShareCustomColors[BrandMappedColorRole]>>;
  /** The matched hex per displayed role, for the transient preview before the save round-trips. */
  contextHexByRole: BrandContextHexByRole;
};

/** A brand-kit color (color-type Context Source value) offered as a quick-pick swatch option. */
export type BrandColorEntry = {
  /** The Context Source field value id — what a `contextSource` color ref points at. */
  id: string;
  /** The owning Context Source (brand kit) id — a `contextSource` ref must carry all three ids. */
  contextSourceId: string;
  /** The owning Context Source field id — a `contextSource` ref must carry all three ids. */
  contextSourceFieldId: string;
  label: string;
  /** The brand field this color belongs to (e.g. "Colors"), used as the swatch group header. */
  fieldName: string;
  hex: string;
};

export type ContextSourceGetResult = NonNullable<ReturnType<typeof usePrivateContextSourcesGet>['data']>;

/** Pure mapping from a Context Source's color-type values to brand swatch entries. */
export const mapBrandColors = (source: ContextSourceGetResult): BrandColorEntry[] =>
  source.fields
    .filter((field) => field.valueType === 'color')
    .flatMap((field) =>
      (field.values ?? []).map((value) => ({
        id: value.id,
        contextSourceId: source.id,
        contextSourceFieldId: field.id,
        // `||`, not `??`: the brand kit editor stores an unlabelled value as an EMPTY STRING, which
        // would defeat a nullish fallback and render label-less entries (FOUND-133 QA).
        label: value.label || field.name,
        fieldName: field.name,
        hex: String(value.value),
      })),
    );

/**
 * Pure mapping from a Context Source's color values to share-link color roles. Returns both the
 * Output-shaped contextSource refs to commit and a display hex map for the live preview.
 */
export const mapBrandColorsToRoles = (source: ContextSourceGetResult): BrandColorMappingResult => {
  const colorValues = mapBrandColors(source);

  const nextRoles: BrandColorMappingResult['nextRoles'] = {};
  const contextHexByRole: BrandContextHexByRole = {};
  // Once a value backs a role it can't back another, so distinct roles get distinct brand colors.
  const usedValueIds = new Set<string>();

  for (const role of ['headerBgColor', 'buttonBgColor', 'pageBgColor'] as const) {
    const keywords = BRAND_CONTEXT_ROLE_KEYWORDS[role];

    const match = colorValues.find((colorValue) => {
      if (usedValueIds.has(colorValue.id)) {
        return false;
      }

      // An unlabelled value's label falls back to its field name (see mapBrandColors), which only
      // repeats a string already in the haystack — so keyword matching behaves exactly as before.
      const haystack = `${colorValue.fieldName} ${colorValue.label}`.toLowerCase();

      return keywords.some((keyword) => haystack.includes(keyword));
    });

    if (!match) {
      continue;
    }

    usedValueIds.add(match.id);
    contextHexByRole[role] = match.hex;

    const ref: ShareCustomColors[BrandMappedColorRole] = {
      type: 'contextSource',
      hex: match.hex,
      // The backend requires the full id triple on every contextSource ref (a value id alone is
      // rejected at write time), so the ref carries the owning kit and field ids too.
      contextSourceId: match.contextSourceId,
      contextSourceFieldId: match.contextSourceFieldId,
      contextSourceFieldValueId: match.id,
    };

    nextRoles[role] = ref;

    // Buttons & headlines are authored by one control, so the headline role mirrors the button ref.
    if (role === 'buttonBgColor') {
      nextRoles.headlineBgColor = ref;
    }
  }

  return { nextRoles, contextHexByRole };
};

/**
 * Output-shaped contextSource ref for a picked brand color swatch — the same shape the auto-map
 * commits. It carries the entry's resolved `hex` so the optimistic/draft write previews instantly;
 * the update normalizer (`getShortUrlUpdatePresentationSettings`) strips the hex on the wire so the
 * server re-resolves the color on read.
 */
export const mapBrandColorEntryToColorRef = (
  entry: BrandColorEntry,
): NonNullable<ShareCustomColors[BrandMappedColorRole]> => ({
  type: 'contextSource',
  hex: entry.hex,
  contextSourceId: entry.contextSourceId,
  contextSourceFieldId: entry.contextSourceFieldId,
  contextSourceFieldValueId: entry.id,
});

/** A brand-kit image (file-type Context Source value) offered as a quick-pick logo option. */
export type BrandImageEntry = {
  /** The Context Source field value id — what a `contextSource` customLogo ref points at. */
  id: string;
  /** The owning Context Source (brand kit) id — a `contextSource` ref must carry all three ids. */
  contextSourceId: string;
  /** The owning Context Source field id — a `contextSource` ref must carry all three ids. */
  contextSourceFieldId: string;
  label: string;
  /** The brand field this image belongs to (e.g. "Logos"), used as the dropdown group header. */
  fieldName: string;
  /** Display URL for the image (thumbnail preview + transient logo src). */
  url: string;
};

/** A brand-kit font (font-type Context Source value) offered in the font row's dropdown. */
export type BrandFontEntry = {
  /** The Context Source field value id — what a `contextSource` headlineFont ref points at. */
  id: string;
  /** The owning Context Source (brand kit) id — a `contextSource` ref must carry all three ids. */
  contextSourceId: string;
  /** The owning Context Source field id — a `contextSource` ref must carry all three ids. */
  contextSourceFieldId: string;
  label: string;
  /** The brand field this font belongs to (e.g. "Typography"), used as the dropdown group header. */
  fieldName: string;
  family: string;
  /** URL of the uploaded font file, when the value carries one (built-in fonts omit it). */
  fileUrl?: string;
};

/**
 * Pure mapping from a Context Source's file-type values to logo picker options. Values whose media
 * carries no resolvable image URL (e.g. non-image files) are dropped.
 */
export const mapBrandImages = (source: ContextSourceGetResult): BrandImageEntry[] =>
  source.fields
    .filter((field) => field.valueType === 'file')
    .flatMap((field) =>
      (field.values ?? []).flatMap((value) => {
        const rawValue = value.value;

        if (typeof rawValue === 'string' || !('media' in rawValue)) {
          return [];
        }

        const url = rawValue.media?.image ?? rawValue.media?.original;

        if (!url) {
          return [];
        }

        return [
          {
            id: value.id,
            contextSourceId: source.id,
            contextSourceFieldId: field.id,
            // Empty-string-safe fallback, same as mapBrandColors.
            label: value.label || field.name,
            fieldName: field.name,
            url,
          },
        ];
      }),
    );

/**
 * Pure mapping from a Context Source's font-type values to font dropdown entries. `fileUrl` is only
 * present for uploaded font files (`media.original`); built-in fonts resolve by family name.
 */
export const mapBrandFonts = (source: ContextSourceGetResult): BrandFontEntry[] =>
  source.fields
    .filter((field) => field.valueType === 'font')
    .flatMap((field) =>
      (field.values ?? []).flatMap((value) => {
        const rawValue = value.value;

        if (typeof rawValue === 'string' || !('family' in rawValue)) {
          return [];
        }

        const fileUrl = 'media' in rawValue ? rawValue.media?.original : undefined;

        return [
          {
            id: value.id,
            contextSourceId: source.id,
            contextSourceFieldId: field.id,
            // Empty-string-safe fallback (see mapBrandColors) — an unlabelled font must still name
            // itself, or the dropdown and the row trigger both render blank (FOUND-133 QA).
            label: value.label || rawValue.family,
            fieldName: field.name,
            family: rawValue.family,
            ...(fileUrl ? { fileUrl } : {}),
          },
        ];
      }),
    );

/**
 * Defaults mirror the public share page fallbacks: Air's gray-4-light surface for the header
 * and page backgrounds, and Air's brand blue for buttons & headlines.
 */
export const DEFAULT_HEADER_BG_HEX = '#EFEFEF';
export const DEFAULT_BUTTON_BG_HEX = '#0563E9';
export const DEFAULT_PAGE_BG_HEX = '#EFEFEF';

/**
 * The color-role config (read/write keys + analytics role + default hex), WITHOUT a `testId`. Each
 * consumer wraps this with its own namespaced testId so each authoring surface stays independently
 * testable.
 */
export type ShareDesignColorRoleConfig = Omit<ShareDesignColorRole, 'testId'>;

/*
 * Page width has a backend field (`presentationSettings.pageWidth`) — the option values mirror the
 * wire union verbatim ('narrow' | 'standard' | 'wide'). The board share tab live-saves it and the
 * collection link EDITOR persists it; the collection create modal's control is still preview-only
 * (wiring the create flow is a separate slice).
 *
 * The default comes from the shared `shareLinkPageWidth` module: absent `pageWidth` must mean the
 * same thing on the authoring surfaces as it does in the public renderer's fallback that legacy
 * links depend on.
 */
export const SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS: SegmentedControlOption[] = [
  { label: 'Narrow', value: 'narrow', id: 'narrow', tooltip: 'Narrow content column' },
  { label: 'Standard', value: 'standard', id: 'standard', tooltip: 'Standard content column' },
  { label: 'Wide', value: 'wide', id: 'wide', tooltip: 'Wide content column' },
];
/**
 * The option object backing PAGE_WIDTH_DEFAULT, derived (not indexed) so the providers' default
 * context seeds can never disagree with the value default. The `?? [0]` arm is unreachable while
 * the options mirror the wire union; it only guards a future option/default mismatch.
 */
export const DEFAULT_PAGE_WIDTH_OPTION =
  SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS.find((option) => option.value === PAGE_WIDTH_DEFAULT) ??
  SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS[0];

/*
 * Thumbnail corner radius has a backend field (`presentationSettings.thumbnailRadius`) — the option
 * values mirror the wire union verbatim ('standard' | 'subtle' | 'square'). The board share tab and
 * the collection EDIT modal live-save it and the collection link EDITOR commits it to its draft;
 * the collection create modal's control is still preview-only local state (wiring the create flow
 * is a separate slice).
 *
 * The default comes from the shared `shareLinkThumbnailRadius` module: absent `thumbnailRadius`
 * must mean the same thing on the authoring surfaces as it does in the public renderer's fallback
 * that legacy links depend on.
 */
export const SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS: SegmentedControlOption[] = [
  { label: 'Standard', value: 'standard', id: 'standard', tooltip: 'Rounded thumbnail corners' },
  { label: 'Subtle', value: 'subtle', id: 'subtle', tooltip: 'Slightly rounded thumbnail corners' },
  { label: 'Square', value: 'square', id: 'square', tooltip: 'Square thumbnail corners' },
];
/**
 * The option object backing THUMBNAIL_RADIUS_DEFAULT, derived (not indexed) so the providers'
 * default context seeds can never disagree with the value default. The `?? [0]` arm is unreachable
 * while the options mirror the wire union; it only guards a future option/default mismatch.
 */
export const DEFAULT_THUMBNAIL_RADIUS_OPTION =
  SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS.find((option) => option.value === THUMBNAIL_RADIUS_DEFAULT) ??
  SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS[0];

/*
 * Color mode (the "Page theme" control) has a backend field (`presentationSettings.colorMode`). The
 * board share tab and the collection EDIT modal live-save it and the collection link EDITOR commits
 * it to its draft; the collection create modal's control is still preview-only local state (wiring
 * the create flow is a separate slice).
 *
 * The options deliberately offer only 'light' and 'dark' even though the wire union also carries
 * 'toggle' — a viewer-facing theme toggle is a named follow-up with no designed authoring control.
 * A stored 'toggle' degrades gracefully: the selected-option lookups fall back to the first option.
 *
 * The default comes from the shared `shareLinkColorMode` module: absent `colorMode` must mean the
 * same thing on the authoring surfaces as it does in the public renderer's fallback that legacy
 * links depend on.
 */
export const SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS: SegmentedControlOption[] = [
  { label: 'Light', value: 'light', id: 'light', tooltip: 'Light page theme' },
  { label: 'Dark', value: 'dark', id: 'dark', tooltip: 'Dark page theme' },
];
/**
 * The option object backing COLOR_MODE_DEFAULT, derived (not indexed) so the providers' default
 * context seeds can never disagree with the value default. Unlike its page-width/thumbnail-radius
 * twins, the `?? [0]` arm here is reachable by design: the options deliberately omit the wire
 * union's 'toggle' member (see above), so it also covers a future default of 'toggle'.
 */
export const DEFAULT_COLOR_MODE_OPTION =
  SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS.find((option) => option.value === COLOR_MODE_DEFAULT) ??
  SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS[0];

/** Uncommitted picker hexes, keyed by each role's read key, driving the live preview only. */
export type TransientColors = { [Key in keyof ShareCustomColors]?: string };

/**
 * Resolves a single role's display hex via the load-bearing fallback chain:
 * transient ?? stored ?? brand-context ?? default. Keep this order EXACTLY — it determines which
 * source wins for the live preview.
 */
export const resolveRoleHex = (
  readKey: keyof ShareCustomColors,
  transientColors: TransientColors,
  customColors: ShareCustomColors | undefined,
  contextHexByRole: BrandContextHexByRole,
  defaultHex: string,
): string => transientColors[readKey] ?? customColors?.[readKey]?.hex ?? contextHexByRole[readKey] ?? defaultHex;

/**
 * Resolves a color row's display props (display hex + custom flag + backing brand value id) for a
 * given role, so the per-role color row components stay thin and don't recompute the resolution
 * chain.
 */
export const resolveColorRowProps = ({
  role,
  transientColors,
  customColors,
  contextHexByRole,
}: {
  role: ShareDesignColorRoleConfig;
  transientColors: TransientColors;
  customColors: ShareCustomColors | undefined;
  contextHexByRole: BrandContextHexByRole;
}): { displayHex: string; isCustomColor: boolean; selectedBrandColorId: string | undefined } => {
  const storedColor = customColors?.[role.readKey];

  return {
    displayHex: resolveRoleHex(role.readKey, transientColors, customColors, contextHexByRole, role.defaultHex),
    isCustomColor: !!storedColor,
    // The brand value id backing the stored color when it's a contextSource ref, so the row can
    // label the swatch by identity instead of hex coincidence (see ShareDesignColorRow).
    selectedBrandColorId: storedColor?.type === 'contextSource' ? storedColor.contextSourceFieldValueId : undefined,
  };
};
